import moment from "moment";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import filterReducer from "./FilterReducer";
import { useReducer, useState, createContext, useEffect, useRef, useMemo } from "react";
import { filterInitialState, intervalPresets } from "./initialStates";
import { constants } from "./constants";
import { FilterState, FilterAction } from "./types";
import {
  computeColorAssignment,
  ColoringLevel,
  DataScienceColorState,
} from "../utils/colorAssignment";
import { getVariableById } from "../utils/variableMetadata";

export interface DataScienceContextType {
  pid?: string;

  filterState: FilterState;
  filterDispatch: React.Dispatch<FilterAction>;

  lYaxis: any[];
  rYaxis: any[];
  setLYaxis: (data: any[]) => void;
  setRYaxis: (data: any[]) => void;

  lYaxisMean: any[];
  rYaxisMean: any[];
  setLYaxisMean: (data: any[]) => void;
  setRYaxisMean: (data: any[]) => void;

  startDate: string;
  endDate: string;
  initialStartDate: string;
  initialEndDate: string;
  activeDate: number;
  setStartDate: (v: string) => void;
  setEndDate: (v: string) => void;
  setInitialStartDate: (v: string) => void;
  setInitialEndDate: (v: string) => void;
  setActiveDate: (v: number) => void;

  dragItemLeft: string | null;
  dragItemRight: string | null;
  setDragItemLeft: (v: string | null) => void;
  setDragItemRight: (v: string | null) => void;
  dragItemLeftId: number | null;
  dragItemRightId: number | null;
  setDragItemLeftId: (v: number | null) => void;
  setDragItemRightId: (v: number | null) => void;
  positionLeft: boolean;
  positionRight: boolean;
  setPositionLeft: (v: boolean) => void;
  setPositionRight: (v: boolean) => void;
  draggableClicked: boolean;
  setDraggableClicked: (v: boolean) => void;

  activeMean: boolean;
  healthData: any[];
  setHealthData: (data: any[]) => void;
  setActiveMean: (v: boolean) => void;
  activeHealthData: boolean;
  setActiveHealthData: (v: boolean) => void;
  activeIrrigationData: boolean;
  setActiveIrrigationData: (v: boolean) => void;
  showMoistureThresholds: boolean;
  setShowMoistureThresholds: (v: boolean) => void;
  hasMoistureThresholdContext: boolean;
  thresholdTree: any | null;
  activeRain: boolean;
  setActiveRain: (v: boolean) => void;
  allResults: any[];
  setAllResults: (v: any[]) => void;
  allResultsMean: any[];
  setAllResultsMean: (v: any[]) => void;
  series: any[];
  setSeries: (v: any[]) => void;
  showIntervalPicker: boolean;
  setShowIntervalPicker: (v: boolean) => void;
  zoomTriggeredDateChange: boolean;
  setZoomTriggeredDateChange: (v: boolean) => void;

  coloringLevel: ColoringLevel;
  areaColorMap: Record<number | string, string>;
  treeColorMap: Record<number | string, string>;
  installationColorMap: Record<number | string, string>;
  lineColor: Record<number | string, string>;
  treeLineColor: Record<number | string, string>;
  setLineColor: (v: Record<number, string>) => void;
  setTreeLineColor: (v: Record<number, string>) => void;

  activeSeriesColorMap: Record<number | string, string>;
  setActiveSeriesColorMap: (v: Record<number | string, string>) => void;

  activeInstallations: any[];
  setActiveInstallations: (v: any[]) => void;

  chartType: string;
  setChartType: (v: string) => void;
  customInterval: string | null;
  setCustomInterval: (v: string | null) => void;
  intervalPresets: any;
  strokewidth: {
    serial: number;
  };
  setStrokeWidth: (v: { serial: number }) => void;

  showHideLeftData: boolean;
  showHideRightData: boolean;
  setHideLeftData: (v: boolean) => void;
  setHideRightData: (v: boolean) => void;

  mobileToolsOpen: boolean;
  setMobileToolsOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
}

export const DataScienceContext = createContext<DataScienceContextType | null>(null);

function DataScienceProvider({ children }) {
  const [filterState, filterDispatch] = useReducer<React.Reducer<FilterState, FilterAction>>(
    filterReducer,
    filterInitialState
  );
  const [draggableClicked, setDraggableClicked] = useState(false);
  const [chartType, setChartType] = useState("line");
  const [activeMean, setActiveMean] = useState(false);
  const [activeHealthData, setActiveHealthData] = useState(false);
  const [activeIrrigationData, setActiveIrrigationData] = useState(false);
  const [showMoistureThresholds, setShowMoistureThresholds] = useState(false);
  const [positionLeft, setPositionLeft] = useState(false);
  const [positionRight, setPositionRight] = useState(false);
  const [dragItemLeft, setDragItemLeft] = useState<string | null>(null);
  const [dragItemRight, setDragItemRight] = useState<string | null>(null);
  const [dragItemLeftId, setDragItemLeftId] = useState<number | null>(null);
  const [dragItemRightId, setDragItemRightId] = useState<number | null>(null);
  const [customInterval, setCustomInterval] = useState<string | null>(null);
  const [showIntervalPicker, setShowIntervalPicker] = useState(false);
  const [zoomTriggeredDateChange, setZoomTriggeredDateChange] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [initialStartDate, setInitialStartDate] = useState<string>("");
  const [activeRain, setActiveRain] = useState(false);
  const [lYaxis, setLYaxis] = useState([]);
  const [rYaxis, setRYaxis] = useState([]);

  const originalSetLYaxis = setLYaxis;
  const originalSetRYaxis = setRYaxis;

  const debugSetLYaxis = (newData) => {
    originalSetLYaxis(newData);
  };

  const debugSetRYaxis = (newData) => {
    originalSetRYaxis(newData);
  };
  const [lYaxisMean, setLYaxisMean] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [allResultsMean, setAllResultsMean] = useState([]);
  const [rYaxisMean, setRYaxisMean] = useState([]);
  const [healthData, setHealthData] = useState([]);
  const [endDate, setEndDate] = useState<string>("");
  const [initialEndDate, setInitialEndDate] = useState<string>("");
  const colorState = useMemo<DataScienceColorState>(
    () =>
      computeColorAssignment(
        filterState.areasId,
        filterState.treesId,
        filterState.installationsId,
        filterState.areas,
        filterState.installations
      ),
    [
      filterState.areasId,
      filterState.treesId,
      filterState.installationsId,
      filterState.areas,
      filterState.installations,
    ]
  );
  const { coloringLevel, areaColorMap, treeColorMap, installationColorMap } = colorState;
  const lineColor = installationColorMap;
  const treeLineColor = treeColorMap;

  const setLineColor = (_v: Record<number, string>) => undefined;
  const setTreeLineColor = (_v: Record<number, string>) => undefined;
  const [activeSeriesColorMap, setActiveSeriesColorMap] = useState<Record<number | string, string>>(
    {}
  );
  const [activeDate, setActiveDate] = useState(2);
  const [activeInstallations, setActiveInstallations] = useState([]);
  const [series, setSeries] = useState([]);
  const [strokewidth, setStrokeWidth] = useState({
    serial: 1,
  });
  const [showHideLeftData, setHideLeftData] = useState(false);
  const [showHideRightData, setHideRightData] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const thresholdInstallation = useMemo(() => {
    const selectedInstallationIds = Array.isArray(filterState?.installationsId)
      ? filterState.installationsId
      : [];
    if (selectedInstallationIds.length !== 1) return null;
    const selectedInstallationId = selectedInstallationIds[0];
    return (
      (activeInstallations || []).find(
        (installation) => installation?.id === selectedInstallationId
      ) ||
      (filterState.installations || []).find(
        (installation) => installation?.id === selectedInstallationId
      ) ||
      null
    );
  }, [activeInstallations, filterState?.installations, filterState?.installationsId]);

  const thresholdTree = useMemo(() => {
    if (!thresholdInstallation) return null;
    const treeId = thresholdInstallation.tree?.id;
    if (!treeId) return null;
    const filterTrees = Array.isArray(filterState?.trees) ? filterState.trees : [];
    return filterTrees.find((tree) => tree?.id === treeId) || null;
  }, [thresholdInstallation, filterState?.trees]);
  const hasMoistureThresholdContext = Boolean(thresholdInstallation && thresholdTree);

  const { pid } = useParams();

  const location = useLocation();
  const navigate = useNavigate();

  const parseIds = (v: string | null) => {
    if (!v) return [];
    return v.split(",").map((x) => {
      const num = Number(x);
      return Number.isFinite(num) ? num : x;
    });
  };

  const isHydratingFromUrl = useRef(false);

  useEffect(() => {
    isHydratingFromUrl.current = true;
    const p = new URLSearchParams(location.search);

    filterDispatch({ type: constants.RESET_CHECKED_AREAS, payload: parseIds(p.get("areas")) });
    filterDispatch({ type: constants.RESET_CHECKED_TREES, payload: parseIds(p.get("trees")) });
    filterDispatch({
      type: constants.RESET_CHECKED_INSTALLATIONS,
      payload: parseIds(p.get("installations")),
    });
    filterDispatch({
      type: constants.RESET_CHECKED_VARIABLES,
      payload: parseIds(p.get("variables")),
    });

    const parseSpecialId = (val: string | null) => {
      if (!val) return null;
      const num = Number(val);
      return Number.isFinite(num) ? num : val;
    };

    filterDispatch({
      type: constants.SET_RAIN_AREA,
      payload: parseSpecialId(p.get("rainAreaId")),
    });
    filterDispatch({
      type: constants.SET_HEALTH_TREE,
      payload: parseSpecialId(p.get("healthTreeId")),
    });
    filterDispatch({
      type: constants.SET_IRRIGATION_TREE,
      payload: parseSpecialId(p.get("irrigationTreeId")),
    });

    const leftVarIdRaw = p.get("leftVarId");
    const rightVarIdRaw = p.get("rightVarId");

    if (leftVarIdRaw != null) {
      const id = Number(leftVarIdRaw);
      if (Number.isFinite(id)) {
        setDragItemLeftId(id);
        const def = getVariableById(id);
        if (def) setDragItemLeft(def.field);
      }
    } else {
      setDragItemLeft(p.get("leftVar") || null);
      setDragItemLeftId(null);
    }

    if (rightVarIdRaw != null) {
      const id = Number(rightVarIdRaw);
      if (Number.isFinite(id)) {
        setDragItemRightId(id);
        const def = getVariableById(id);
        if (def) setDragItemRight(def.field);
      }
    } else {
      setDragItemRight(p.get("rightVar") || null);
      setDragItemRightId(null);
    }

    setChartType(p.get("chartType") || "line");
    setActiveMean(p.get("activeMean") === "true");
    setActiveHealthData(p.get("activeHealthData") === "true");
    setActiveIrrigationData(p.get("activeIrrigationData") === "true");
    setActiveRain(p.get("activeRain") === "true");

    setActiveDate(p.get("activeDate") ? Number(p.get("activeDate")) : 2);
    setCustomInterval(p.get("interval") || null);

    const urlStart = p.get("startDate");
    const urlEnd = p.get("endDate");
    if (urlStart && urlEnd) {
      setStartDate(urlStart.trim());
      setEndDate(urlEnd.trim());
      setInitialStartDate(urlStart.trim());
      setInitialEndDate(urlEnd.trim());
    } else {
      const defStart = moment().subtract(3, "weeks").format("YYYY-MM-DD");
      const defEnd = moment().format("YYYY-MM-DD");
      setStartDate(defStart);
      setEndDate(defEnd);
      setInitialStartDate(defStart);
      setInitialEndDate(defEnd);
    }

    setPositionLeft(Boolean(p.get("leftVarId") || p.get("leftVar")));
    setPositionRight(Boolean(p.get("rightVarId") || p.get("rightVar")));

    queueMicrotask(() => {
      isHydratingFromUrl.current = false;
    });
  }, [location.search]);

  useEffect(() => {
    if (isHydratingFromUrl.current) return;

    const DS_KEYS = [
      "leftVar",
      "rightVar",
      "leftVarId",
      "rightVarId",
      "chartType",
      "activeMean",
      "activeHealthData",
      "activeIrrigationData",
      "activeRain",
      "activeDate",
      "interval",
      "startDate",
      "endDate",
      "installations",
      "areas",
      "trees",
      "variables",
      "rainAreaId",
      "healthTreeId",
      "irrigationTreeId",
    ];

    const current = new URLSearchParams(location.search);
    const next = new URLSearchParams(location.search);

    [...new Set(DS_KEYS)].forEach((k) => next.delete(k));

    if (dragItemLeft) next.set("leftVar", String(dragItemLeft));
    if (dragItemRight) next.set("rightVar", String(dragItemRight));
    if (dragItemLeftId != null) next.set("leftVarId", String(dragItemLeftId));
    if (dragItemRightId != null) next.set("rightVarId", String(dragItemRightId));

    if (chartType && chartType !== "line") next.set("chartType", chartType);
    if (activeMean) next.set("activeMean", "true");
    if (activeHealthData) next.set("activeHealthData", "true");
    if (activeIrrigationData) next.set("activeIrrigationData", "true");
    if (activeRain) next.set("activeRain", "true");

    if (activeDate !== 2) next.set("activeDate", String(activeDate));
    if (customInterval) next.set("interval", String(customInterval));

    if (startDate && endDate) {
      const defaultStart = moment().subtract(3, "weeks").format("YYYY-MM-DD");
      const defaultEnd = moment().format("YYYY-MM-DD");
      if (startDate !== defaultStart || endDate !== defaultEnd) {
        next.set("startDate", String(startDate).trim());
        next.set("endDate", String(endDate).trim());
      }
    }

    if (filterState.areasId.length) next.set("areas", filterState.areasId.join(","));
    if (filterState.treesId.length) next.set("trees", filterState.treesId.join(","));
    if (filterState.installationsId.length)
      next.set("installations", filterState.installationsId.join(","));
    if (filterState.variablesId.length) {
      next.set("variables", filterState.variablesId.join(","));
    } else {
      const dragSelected = [dragItemLeft, dragItemRight].filter(Boolean);
      const dragVariableIds = dragSelected
        .map((label) => filterState.variables.find((v) => v.label === label)?.id)
        .filter((id) => Number.isFinite(id));
      if (dragVariableIds.length) {
        next.set("variables", Array.from(new Set(dragVariableIds)).join(","));
      }
    }

    if (filterState.rainAreaId !== null) next.set("rainAreaId", String(filterState.rainAreaId));
    if (filterState.healthTreeId !== null)
      next.set("healthTreeId", String(filterState.healthTreeId));
    if (filterState.irrigationTreeId !== null)
      next.set("irrigationTreeId", String(filterState.irrigationTreeId));

    if (current.toString() !== next.toString()) {
      navigate(
        { pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" },
        { replace: true }
      );
    }
  }, [
    location.pathname,
    navigate,
    dragItemLeft,
    dragItemRight,
    dragItemLeftId,
    dragItemRightId,
    chartType,
    activeMean,
    activeHealthData,
    activeIrrigationData,
    activeRain,
    activeDate,
    customInterval,
    startDate,
    endDate,
    filterState.areasId,
    filterState.treesId,
    filterState.installationsId,
    filterState.variablesId,
    filterState.rainAreaId,
    filterState.healthTreeId,
    filterState.irrigationTreeId,
  ]);

  useEffect(() => {
    if (dragItemLeft && !positionLeft) {
      setPositionLeft(true);
    }
    if (!dragItemLeft && positionLeft) {
      setPositionLeft(false);
    }
  }, [dragItemLeft, positionLeft]);

  useEffect(() => {
    if (dragItemRight && !positionRight) {
      setPositionRight(true);
    }
    if (!dragItemRight && positionRight) {
      setPositionRight(false);
    }
  }, [dragItemRight, positionRight]);

  useEffect(() => {
    if (dragItemLeftId == null) {
      setDragItemLeft(null);
    }
  }, [dragItemLeftId]);

  useEffect(() => {
    if (dragItemRightId == null) {
      setDragItemRight(null);
    }
  }, [dragItemRightId]);

  useEffect(() => {
    if (!dragItemLeft) {
      console.log("CLEARING LYAXIS - dragItemLeft is:", dragItemLeft);
      debugSetLYaxis([]);
      setLYaxisMean([]);
    }
  }, [dragItemLeft]);

  useEffect(() => {
    if (!dragItemRight) {
      debugSetRYaxis([]);
      setRYaxisMean([]);
    }
  }, [dragItemRight]);

  useEffect(() => {
    if (!hasMoistureThresholdContext) {
      setShowMoistureThresholds(false);
    }
  }, [hasMoistureThresholdContext]);

  return (
    <DataScienceContext.Provider
      value={{
        filterState,
        filterDispatch,
        pid,
        lYaxis,
        rYaxis,
        setLYaxis: debugSetLYaxis,
        setRYaxis: debugSetRYaxis,
        lYaxisMean,
        rYaxisMean,
        setLYaxisMean,
        setRYaxisMean,
        dragItemLeft,
        dragItemRight,
        setDragItemLeft,
        setDragItemRight,
        dragItemLeftId,
        dragItemRightId,
        setDragItemLeftId,
        setDragItemRightId,
        initialStartDate,
        initialEndDate,
        setInitialStartDate,
        setInitialEndDate,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        lineColor,
        treeLineColor,
        setLineColor,
        setTreeLineColor,
        setPositionLeft,
        setPositionRight,
        setActiveRain,
        activeRain,
        strokewidth,
        setStrokeWidth,
        setActiveDate,
        activeDate,
        healthData,
        setHealthData,
        activeMean,
        setActiveMean,
        positionLeft,
        positionRight,
        activeHealthData,
        setActiveHealthData,
        activeIrrigationData,
        setActiveIrrigationData,
        showMoistureThresholds,
        setShowMoistureThresholds,
        hasMoistureThresholdContext,
        thresholdTree,
        chartType,
        setChartType,
        setDraggableClicked,
        draggableClicked,
        activeInstallations,
        setActiveInstallations,
        setAllResults,
        setAllResultsMean,
        allResults,
        allResultsMean,
        setSeries,
        series,
        customInterval,
        setCustomInterval,
        showIntervalPicker,
        setShowIntervalPicker,
        zoomTriggeredDateChange,
        setZoomTriggeredDateChange,
        intervalPresets,
        showHideLeftData,
        showHideRightData,
        setHideLeftData,
        setHideRightData,
        coloringLevel,
        areaColorMap,
        treeColorMap,
        installationColorMap,
        activeSeriesColorMap,
        setActiveSeriesColorMap,
        mobileToolsOpen,
        setMobileToolsOpen,
      }}
    >
      {children}
    </DataScienceContext.Provider>
  );
}

export default DataScienceProvider;
